import maplibregl from "maplibre-gl";
import polylabel from "polylabel";

const Features = {
  manhattan: "189390c5-b02d-4f4a-88da-14a9b54feafa",
  bronx: "caa93cfd-865a-44b1-abd9-96aca9cf0a60",
};

const Colors = {
  defaultFill: "#37FFF3",
  zoneFills: {
    0: "#FAFF00",
    1: "#4C6682",
    2: "#FF9B52",
    3: "#FF71D4",
    4: "#0081FF",
  },

  defaultBorder: "#31B1AA",
  zoneBorders: {
    0: "#FBB502",
    1: "#4C6682",
    2: "#F1684D",
    3: "#E329AB",
    4: "#0081FF",
  },
};

class Plots {
  constructor(map, apiKey, onClick) {
    this.map = map;
    this.apiKey = apiKey;
    this.onSelect = onClick;

    this.load();
    this.draw();
    this.handleHover();
    this.handleClick();
  }

  load() {
    var featureIds = [Features.manhattan, Features.bronx];
    var result = { type: "FeatureCollection", features: [] };
    featureIds.forEach(async (id) => {
      const response = await fetch(`https://api.maptiler.com/data/${id}/features.json?key=${this.apiKey}`, {
        method: "GET",
      });

      const json = await response.json();
      const features = json.features.filter((feature) => {
        return Boolean(feature.properties.zone);
      });
      result.features = result.features.concat(features);

      const source = this.map.getSource("plots");
      if (source) {
        source.setData(result);
      }
    });
  }

  draw() {
    this.map.addSource("plots", {
      type: "geojson",
      generateId: true,
      data: { type: "FeatureCollection", features: [] },
    });

    this.map.addLayer({
      id: "plots-fills",
      type: "fill",
      source: "plots",
      layout: {},
      minzoom: 11,
      paint: {
        "fill-color": ["to-color", ["feature-state", "fill-color"], Colors.defaultFill],
        "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.9, 0.5],
      },
    });

    this.map.addLayer({
      id: "plots-borders",
      type: "line",
      source: "plots",
      layout: {},
      minzoom: 11,
      paint: {
        "line-color": ["to-color", ["feature-state", "border-color"], Colors.defaultBorder],
        "line-width": 1,
      },
    });
  }

  handleHover() {
    var hoveredStateId = null;

    this.map.on("mousemove", "plots-fills", (e) => {
      if (e.features.length > 0) {
        if (hoveredStateId) {
          this.map.setFeatureState({ source: "plots", id: hoveredStateId }, { hover: false });
        }
        hoveredStateId = e.features[0].id;

        this.map.setFeatureState({ source: "plots", id: hoveredStateId }, { hover: true });
      }
    });

    this.map.on("mouseleave", "plots-fills", () => {
      if (hoveredStateId) {
        this.map.setFeatureState({ source: "plots", id: hoveredStateId }, { hover: false });
      }
      hoveredStateId = null;
    });
  }

  handleClick() {
    this.map.on("click", "plots-fills", (e) => {
      const clickedFeature = e.features[0];
      this._selectFeature(clickedFeature);
    });
  }

  removeMarker(marker) {
    this.markedFeature = null;

    const m = marker || this.marker;
    if (m) {
      m.remove();
    }
  }

  selectZone(zoneId) {
    this.selectedZoneId = zoneId;

    const getTiledPlots = () => {
      return this.map.querySourceFeatures("plots");
    };

    if (this.selectedZoneId) {
      this.map.on("render", () => {
        if (!this.map.isSourceLoaded("plots")) return;

        const renderedPlots = getTiledPlots();
        if (renderedPlots.length === this.recoloredPlotsCount) return;

        this._recolorPlots(renderedPlots);
        this.recoloredPlotsCount = renderedPlots.length;
      });
    } else {
      this.map.off("render");
    }

    this._recolorPlots(getTiledPlots());
  }

  selectPlot(landId) {
    const feature = this._findFeature(landId);

    if (this.markedFeature?.id === feature.id) {
      this.onSelect(feature);
      return;
    }

    if (feature) this._selectFeature(feature);
  }

  getMarker() {
    return this.marker;
  }

  _findFeature(landId) {
    const feature = this.map.querySourceFeatures("plots", {
      filter: ["==", ["get", "id"], landId],
    })[0];

    if (feature) return feature;

    // workaround to find the feature that doesn't rendered at this time
    // not sure why `querySourceFeatures` looks only for rendered...
    // but it uses private `_data` and I'm not sure it's ok for long-term
    const plots = this.map.getSource("plots");
    return plots._data.features.find((f) => f.properties.id === landId);
  }

  _selectFeature(feature) {
    if (this.marker) this.marker.remove();

    if (this.markedFeature && this.markedFeature.id === feature.id) {
      this.markedFeature = null;
      this.onSelect(null);
      return;
    }

    this.markedFeature = feature;

    const poly = polylabel(feature.geometry.coordinates);
    this.marker = new maplibregl.Marker({
      color: "#38394F",
    })
      .setLngLat(poly)
      .addTo(this.map);

    this.onSelect(feature);
  }

  _recolorPlots(plots) {
    plots.forEach((p) => {
      const zoneId = this.selectedZoneId;
      const matched = Number(p.properties.zone) === zoneId;
      const fillColor = matched ? Colors.zoneFills[zoneId] : null;
      const borderColor = matched ? Colors.zoneBorders[zoneId] : null;

      this.map.setFeatureState(
        { source: "plots", id: p.id },
        {
          "fill-color": fillColor,
          "border-color": borderColor,
        }
      );
    });
  }
}

export default Plots;
